import { useState } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function Auth({ mode }) {
  const isRegister = mode === "register";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [sp] = useSearchParams();
  const next = sp.get("next");
  const [form, setForm] = useState({ email: location.state?.email || "", password: "", display_name: "", username: "", date_of_birth: "", role: "creator" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      if (isRegister) {
        const age = form.date_of_birth ? (new Date().getFullYear() - new Date(form.date_of_birth).getFullYear()) : 0;
        if (!form.date_of_birth || age < 18) { setErr("You must be at least 18 years old to create an account."); setBusy(false); return; }
        const u = await register(form);
        toast.success("Welcome to PictureThisInk!");
        nav(next || (u.role === "advertiser" ? "/advertiser" : "/dashboard"));
      } else {
        const u = await login(form.email, form.password);
        toast.success("Signed in.");
        nav(next || (u.role === "admin" ? "/admin" : u.role === "advertiser" ? "/advertiser" : "/dashboard"));
      }
    } catch (e2) { setErr(apiErr(e2)); }
    setBusy(false);
  };

  return (
    <div className="pti-container py-16 max-w-md" data-testid="auth-page">
      <h1 className="font-display text-4xl font-black">{isRegister ? "Join PictureThisInk" : "Welcome back"}</h1>
      <p className="text-gray-600 mt-1">{isRegister ? "Create an account to publish, follow, and advertise." : "Sign in to your account."}</p>

      {err && <div role="alert" data-testid="auth-error" className="mt-4 border-l-4 border-pti-red bg-red-50 text-pti-red px-4 py-3 text-sm font-semibold">{err}</div>}

      <button type="button" onClick={googleLogin} data-testid="google-login-btn"
        className="mt-6 w-full flex items-center justify-center gap-3 border border-gray-300 py-3 font-bold hover:border-pti-black hover:bg-gray-50 transition-colors">
        <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
        Continue with Google
      </button>

      <div className="flex items-center gap-3 my-6" aria-hidden="true">
        <span className="flex-1 h-px bg-gray-200" />
        <span className="text-xs font-bold uppercase tracking-widest text-gray-400">or with email</span>
        <span className="flex-1 h-px bg-gray-200" />
      </div>

      <form onSubmit={submit} className="space-y-4">
        {isRegister && (
          <>
            <Field label="Display name" testid="reg-name"><input required value={form.display_name} onChange={set("display_name")} className="pti-input" /></Field>
            <Field label="Username" testid="reg-username"><input required value={form.username} onChange={set("username")} placeholder="yourhandle" className="pti-input" /></Field>
          </>
        )}
        <Field label="Email" testid="auth-email"><input required type="email" value={form.email} onChange={set("email")} className="pti-input" /></Field>
        <Field label="Password" testid="auth-password"><input required type="password" minLength={6} value={form.password} onChange={set("password")} className="pti-input" /></Field>
        {isRegister && (
          <>
            <Field label="Date of birth (must be 18+)" testid="reg-dob"><input required type="date" value={form.date_of_birth} onChange={set("date_of_birth")} className="pti-input" /></Field>
            <div>
              <label className="text-sm font-bold">I want to</label>
              <div className="flex gap-2 mt-1">
                {[["creator", "Create & publish"], ["advertiser", "Advertise a brand"]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setForm({ ...form, role: v })} data-testid={`reg-role-${v}`}
                    className={`flex-1 py-2 text-sm font-bold border transition-colors ${form.role === v ? "bg-pti-black text-white border-pti-black" : "border-gray-300 hover:border-pti-black"}`}>{l}</button>
                ))}
              </div>
            </div>
          </>
        )}
        <button type="submit" disabled={busy} data-testid="auth-submit" className="w-full bg-pti-red text-white py-3 font-bold hover:bg-pti-redhover transition-colors disabled:opacity-50">
          {busy ? "Please wait…" : isRegister ? "Create account" : "Sign in"}
        </button>
      </form>

      <p className="text-sm text-gray-500 mt-4">
        {isRegister ? <>Already have an account? <Link to="/login" className="text-pti-red font-bold">Sign in</Link></> : <>New here? <Link to="/register" className="text-pti-red font-bold">Create an account</Link></>}
      </p>
      <style>{`.pti-input{width:100%;border:1px solid #d1d5db;padding:0.6rem 0.75rem;outline:none}.pti-input:focus{border-color:#ef1831}`}</style>
    </div>
  );
}

function Field({ label, children, testid }) {
  return <label className="block" data-testid={testid}><span className="text-sm font-bold">{label}</span><div className="mt-1">{children}</div></label>;
}
